import { memo, MouseEventHandler, SetStateAction, useMemo } from 'react';
import { Selection } from 'src/projects/types/Selection';
import { PATHS } from 'src/types/Paths';
import { getAppRoutePrefix } from 'src/projects/utils/getAppRoutePrefix';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu/OverflowMenu';
import { MoreIcon } from 'src/modules/common/icons/MoreIcon';
import { OverflowMenuItem } from 'src/modules/common/components/OverflowMenu/OverflowMenuItem';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MESSAGES } from 'src/modules/common/constants';
import { CopyIcon } from 'src/modules/common/icons/CopyIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { FetchError } from 'src/projects/components/FetchError';
import { FetchValveSelection } from 'src/projects/components/FetchValveSelection';
import { FetchActuatorSelection } from 'src/projects/components/FetchActuatorSelection';
import { FetchSensorSelection } from 'src/projects/components/FetchSensorSelection';

type Props = {
    selection: Selection;
    openedSelection: string | null;
    setOpenedSelection: (name: SetStateAction<string | null>) => void;
    removeHandler: (selection: Selection) => void;
}

export const ProjectSelectionItem = memo(({ selection, openedSelection, setOpenedSelection, removeHandler }: Props) => {
    const openUrl = useMemo(() => {
        const url = new URL(window.location.toString());
        url.pathname = getAppRoutePrefix(window.location, selection.type);
        url.hash = `${PATHS.Share}?s=${encodeURIComponent(JSON.stringify(selection))}`;
        return url.toString();
    }, [selection]);

    const handleCopySelection: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(openUrl)
        // @TODO add UI support of copy success/error?
        // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    };

    const handleRemoveSelection: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
        removeHandler(selection);
    };

    const handleOpenSelection = () => {
        setOpenedSelection((prev) => (prev === selection.name
            ? null
            : selection.name
        ));
    };
    return (
        <div className="vss-project-selection-item">
            <div
                className="vss-project-selection-item__header"
                onClick={handleOpenSelection}
                role="button"
                tabIndex={0}
                onKeyDown={() => null}
            >
                <div className="vss-project-selection-item__title">
                    {selection.name}
                </div>

                <div className="vss-project-selection-item__details">
                    <div className="vss-project-selection-item__date">
                        {selection.updatedAt.toLocaleString()}
                    </div>

                    <OverflowMenu
                        kind="ghost"
                        buttonSize="md"
                        icon={<MoreIcon/>}
                    >
                        <OverflowMenuItem
                            size="md"
                            onClick={handleCopySelection}
                            reversed={true}
                            icon={(
                                <div className="vss-project-selection-item__icon">
                                    <CopyIcon/>
                                </div>
                            )}
                        >
                            <I18n messageId={MESSAGES.projectPageCopySelection}/>
                        </OverflowMenuItem>

                        <OverflowMenuItem
                            size="md"
                            kind="danger"
                            onClick={handleRemoveSelection}
                            reversed={true}
                            icon={(
                                <div className="vss-project-selection-item__icon">
                                    <DeleteIcon/>
                                </div>
                            )}
                        >
                            <I18n messageId={MESSAGES.projectPageRemoveSelection}/>
                        </OverflowMenuItem>
                    </OverflowMenu>

                    <div className="vss-project-selection-item__icon">
                        {openedSelection === selection.name
                            ? <ChevronUpIcon/>
                            : <ChevronDownIcon/>}
                    </div>
                </div>
            </div>

            <div
                className="vss-project-selection-item__body"
                style={{ display: openedSelection === selection.name
                    ? 'flex'
                    : 'none',
                }}
            >
                <FetchError fallback="Issue with loading selection details">
                    {selection.type === 'valves' && (
                        <FetchValveSelection
                            locationState={selection.locationState}
                            searchParams={selection.searchQuery}
                        />
                    )}
                    {selection.type === 'actuators' && (
                        <FetchActuatorSelection
                            locationState={selection.locationState}
                            searchParams={selection.searchQuery}
                        />
                    )}
                    {selection.type === 'sensors' && (
                        <FetchSensorSelection
                            locationState={selection.locationState}
                            searchParams={selection.searchQuery}
                        />
                    )}
                </FetchError>
            </div>
        </div>
    );
});
